import {
  readModelBodyMetadataStringArray,
  resolveModelBodyIdentity,
} from './model-body-metadata'

/**
 * 设计树投影使用的统一模型体修订状态。
 */
export interface ModelBodyProjectionState {
  /** Worker 当前仍活动的几何修订 ID。 */
  readonly activeRevisionIds: ReadonlySet<string>
  /** 当前结果体明确替换的上游几何修订 ID。 */
  readonly consumedRevisionIds: ReadonlySet<string>
}

/**
 * 设计树模型体节点元数据的最小契约。
 */
export interface ModelBodyTreeNodeMeta {
  /** 节点对象类型，例如 body。 */
  objectType?: string
  /** 节点对应的统一 Worker 几何修订 ID。 */
  modelBodyRevisionId?: string
}

/**
 * 设计树节点的框架无关最小契约。
 */
export interface ModelBodyTreeNode {
  /** 节点 key。 */
  key?: string | number
  /** 节点标签。 */
  label?: string
  /** 子节点。 */
  children?: readonly ModelBodyTreeNode[]
  /** 是否叶子节点。 */
  isLeaf?: boolean
}

/**
 * 统一模型体设计树投影输入。
 */
export interface FilterModelBodyTreeProjectionInput<
  TNode extends ModelBodyTreeNode = ModelBodyTreeNode,
  TMeta extends ModelBodyTreeNodeMeta = ModelBodyTreeNodeMeta,
> {
  /** 待合并到实体文件夹的外部实体节点。 */
  bodyChildren: readonly TNode[]
  /** 外部模型子树顶层节点。 */
  subtreeChildren: readonly TNode[]
  /** 节点元数据映射。 */
  nodeMapEntries: Map<string, TMeta>
  /** Worker 权威模型体修订状态。 */
  projectionState: ModelBodyProjectionState
}

/**
 * 统一模型体设计树投影结果。
 */
export interface FilterModelBodyTreeProjectionResult<
  TNode extends ModelBodyTreeNode = ModelBodyTreeNode,
  TMeta extends ModelBodyTreeNodeMeta = ModelBodyTreeNodeMeta,
> {
  /** 过滤后的实体节点。 */
  bodyChildren: TNode[]
  /** 过滤后的外部模型子树。 */
  subtreeChildren: TNode[]
  /** 同步清理后的节点元数据映射。 */
  nodeMapEntries: Map<string, TMeta>
}

/**
 * 从 Worker 权威对象集合解析统一模型体修订状态。
 *
 * @param objects Worker 当前活动对象。
 * @returns 活动修订与已消费修订集合。
 */
export function resolveModelBodyProjectionState(
  objects: readonly GeometryObject[],
): ModelBodyProjectionState {
  const activeRevisionIds = new Set<string>()
  const consumedRevisionIds = new Set<string>()

  objects.forEach((object) => {
    const identity = resolveModelBodyIdentity(object)
    if (identity) {
      activeRevisionIds.add(identity.modelBodyRevisionId)
    }
    readModelBodyMetadataStringArray(object.metadata?.consumedModelBodyRevisionIds)
      .forEach((revisionId) => consumedRevisionIds.add(revisionId))
  })

  return {
    activeRevisionIds,
    consumedRevisionIds,
  }
}

/**
 * 判断树节点代表的旧修订是否已被正式结果体替换。
 *
 * 缺少统一修订 ID 时保持节点可见，避免加载尚未完成时把实体误删；加载边界必须随后补齐身份。
 *
 * @param modelBodyRevisionId 节点对应的统一修订 ID。
 * @param projectionState Worker 权威修订状态。
 * @returns 修订已消费且不再活动时返回 true。
 */
export function isModelBodyTreeRevisionInactive(
  modelBodyRevisionId: string | undefined,
  projectionState: ModelBodyProjectionState,
): boolean {
  const normalizedRevisionId = modelBodyRevisionId?.trim() ?? ''
  return Boolean(
    normalizedRevisionId
    && !projectionState.activeRevisionIds.has(normalizedRevisionId)
    && projectionState.consumedRevisionIds.has(normalizedRevisionId),
  )
}

/**
 * 解析节点 key。
 *
 * @param node 设计树节点。
 * @returns 字符串 key。
 */
function resolveNodeKey(node: ModelBodyTreeNode): string {
  return typeof node.key === 'string' ? node.key : String(node.key ?? '')
}

/**
 * 递归过滤已经失活的实体节点，并同步刷新实体文件夹计数。
 *
 * @param nodes 当前层节点。
 * @param nodeMapEntries 节点元数据映射。
 * @param projectionState Worker 权威修订状态。
 * @returns 过滤后的当前层节点。
 */
function filterModelBodySubtree<
  TNode extends ModelBodyTreeNode,
  TMeta extends ModelBodyTreeNodeMeta,
>(
  nodes: readonly TNode[],
  nodeMapEntries: Map<string, TMeta>,
  projectionState: ModelBodyProjectionState,
): TNode[] {
  return nodes.flatMap((node) => {
    const key = resolveNodeKey(node)
    const meta = nodeMapEntries.get(key)
    if (
      meta?.objectType === 'body'
      && isModelBodyTreeRevisionInactive(meta.modelBodyRevisionId, projectionState)
    ) {
      nodeMapEntries.delete(key)
      return []
    }

    if (!Array.isArray(node.children) || node.children.length === 0) {
      return [node]
    }

    const filteredChildren = filterModelBodySubtree(
      node.children as readonly TNode[],
      nodeMapEntries,
      projectionState,
    )
    return [{
      ...node,
      children: filteredChildren,
      isLeaf: filteredChildren.length === 0,
      ...(key.endsWith('::bodies') ? { label: `实体(${filteredChildren.length})` } : {}),
    } as TNode]
  })
}

/**
 * 按统一模型体修订状态过滤外部模型设计树投影。
 *
 * @param input 过滤输入。
 * @returns 过滤后的投影片段。
 */
export function filterModelBodyTreeProjection<
  TNode extends ModelBodyTreeNode,
  TMeta extends ModelBodyTreeNodeMeta,
>(
  input: FilterModelBodyTreeProjectionInput<TNode, TMeta>,
): FilterModelBodyTreeProjectionResult<TNode, TMeta> {
  const nodeMapEntries = new Map(input.nodeMapEntries)
  return {
    bodyChildren: filterModelBodySubtree(
      input.bodyChildren,
      nodeMapEntries,
      input.projectionState,
    ),
    subtreeChildren: filterModelBodySubtree(
      input.subtreeChildren,
      nodeMapEntries,
      input.projectionState,
    ),
    nodeMapEntries,
  }
}
